/**
 * Generate Jig utilities module.
 * Re-exports shared stage-tool helpers and provides the small cross-cutting helpers
 * used by auth, model, stage, and result-handling code.
 */
import { AppHelper } from '@/utils/app-helper'
import axios from 'axios'

const WEBSPARK_PREVIEW_VIEWER_URL = 'https://jigworkshop-web.vercel.app/'
const WEBSPARK_PREVIEW_QUERY = '&shad=1&step=1&sbox=0&autoload=1&autoplay=0'

export {
  applyToolsToStage,
  axisAngleToQuaternion,
  findElementByName,
  getMaterialItemsArray,
  hexToRGBA,
} from './generate-jig.stage-tools'

export interface GenerateJigActionStateOptions {
  isJigSparkOptIn: boolean
  isGlobalAIOptOut: boolean
  canGenerateJig: boolean
  isOpeningGenerateJig?: boolean
}

export interface GenerateJigActionState {
  isHidden: boolean
  isDisabled: boolean
  tooltipCopy: string
}

export function isRequestCanceled(error: any): boolean {
  return axios.isCancel(error) || (error && (error.code === 'ERR_CANCELED' || error.name === 'CanceledError'))
}

export function getGenerateJigActionState(options: GenerateJigActionStateOptions): GenerateJigActionState {
  const isOpeningGenerateJig = options.isOpeningGenerateJig === true

  if (!options.isJigSparkOptIn) {
    return {
      isHidden: true,
      isDisabled: true,
      tooltipCopy: '',
    }
  }

  if (options.isGlobalAIOptOut) {
    return {
      isHidden: false,
      isDisabled: true,
      tooltipCopy: 'AI features are disabled for this team. Contact your Team Admin to enable AI.',
    }
  }

  if (!options.canGenerateJig) {
    return {
      isHidden: false,
      isDisabled: true,
      tooltipCopy: 'You do not have permission to create Jigs for this team.',
    }
  }

  if (isOpeningGenerateJig) {
    return {
      isHidden: false,
      isDisabled: true,
      tooltipCopy: '',
    }
  }

  return {
    isHidden: false,
    isDisabled: false,
    tooltipCopy: '',
  }
}

export const DEFAULT_MATERIAL_ID: number = 20
export const MATERIAL_PRESET_TO_ID: Record<string, number> = {
  UserDefined: 20,
  Standard: 20,
  PresetBasic: 20,
  Unlit: 21,
  Flow: 22,
  XRay: 23,
}
export const TEXTURE_TYPE_MAPPING: Record<string, string> = {
  'Base Color': 'diffuse',
  Normal: 'normal',
  Emission: 'emission',
  RMA: 'rma',
}

export function deepClone<T>(value: T): T {
  return AppHelper.deepCopyNestedJsonSafeObject(value)
}

export function normalizeMeshName(name: string): string {
  return name.replace(/\s+/g, '_')
}

export function generateGuid(): string {
  const template = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'
  return template.replace(/[xy]/g, function (char: string) {
    const randomValue = Math.floor(Math.random() * 16)
    const value = char === 'x' ? randomValue : (randomValue & 0x3) | 0x8
    return value.toString(16)
  })
}

export function generateShortUid(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  let uid = ''
  for (let i = 0; i < 8; i++) {
    const index = Math.floor(Math.random() * chars.length)
    uid += chars.charAt(index)
  }
  return uid
}

export function extractJigMeta(jigData: any): any {
  if (!jigData) return null
  // Fetch response uses JigMetaData, copy/PUT response uses JigMetadata
  return jigData.JigMetaData || jigData.JigMetadata || null
}

export function extractDeeplink(jigData: any): string {
  const meta = extractJigMeta(jigData)
  if (meta) {
    return String(meta.DeeplinkURL || meta.deeplinkURL || '')
  }
  // Copy response has top-level deeplinkURL
  if (jigData && jigData.deeplinkURL) {
    return String(jigData.deeplinkURL)
  }

  return ''
}

export function extractJigHashId(jigData: any, deeplink: string): string {
  const meta = extractJigMeta(jigData)
  if (meta) {
    const metaId = String(meta.Id || meta.id || meta.HashId || meta.HashID || '').trim()

    if (metaId.length > 0) {
      return metaId
    }
  }
  // Copy response has top-level id
  if (jigData && jigData.id) {
    return String(jigData.id).trim()
  }

  if (deeplink && deeplink.length > 0) {
    const queryMatch = deeplink.match(/[?&]jig=([A-Za-z0-9]+)/)
    if (queryMatch && queryMatch[1]) {
      return queryMatch[1]
    }
  }

  return ''
}

export function buildViewerBaseUrl(apiDomain: string): string {
  if (!apiDomain) {
    return ''
  }

  try {
    const parsedUrl = new URL(apiDomain)
    const hostParts = parsedUrl.hostname.split('.')

    if (hostParts.length > 0) {
      if (hostParts[0] === 'api') {
        hostParts[0] = 'viewer'
      } else if (hostParts[0].indexOf('api-') === 0) {
        hostParts[0] = hostParts[0].replace(/^api-/, 'viewer-')
      } else {
        hostParts.unshift('viewer')
      }
    }

    return parsedUrl.protocol + '//' + hostParts.join('.')
  } catch (_error) {
    return ''
  }
}

/**
 * Extracts the dashboard env slug from an API domain. Prod (`api.jig.space`)
 * has no env segment; other envs follow `api.{env}.jig.space`.
 */
export function extractEnvFromApiDomain(apiDomain: string): string {
  if (!apiDomain) {
    return ''
  }

  try {
    const hostParts = new URL(apiDomain).hostname.split('.')
    if (hostParts.length >= 4 && hostParts[0] === 'api') {
      return hostParts[1]
    }
    return ''
  } catch (_error) {
    return ''
  }
}

/**
 * Builds the dashboard preview URL, preferring explicit ids from the API payload
 * and falling back to ids embedded in the deeplink when necessary. Non-prod
 * dashboards pass `&env={env}` so the WebSpark viewer targets the matching env.
 */
export function buildPreviewUrl(apiDomain: string, jigData: any, deeplink: string): string {
  const jigHashId = extractJigHashId(jigData, deeplink)

  if (!jigHashId) {
    return ''
  }

  const env = extractEnvFromApiDomain(apiDomain)
  const envQuery = env ? '&env=' + encodeURIComponent(env) : ''

  return WEBSPARK_PREVIEW_VIEWER_URL + '?jig=' + encodeURIComponent(jigHashId) + WEBSPARK_PREVIEW_QUERY + envQuery
}

/**
 * Produces the final Jig copy name, preferring explicit user input and otherwise
 * composing a readable default from the library display name and selected use case.
 */
export function buildJigName(inputName: string, displayName: string, useCase: string): string {
  const trimmed = inputName.trim()
  if (trimmed.length > 0) {
    return trimmed
  }

  const suffix = useCase.trim()
  if (suffix.length > 0) {
    return displayName + ' - ' + suffix
  }

  return displayName + ' - Generated'
}
